import React from 'react'
import { Button } from 'reactstrap'
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
} from '@mui/material'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'

// '' and anything that is not a non-negative whole number is rejected. Number('') is 0,
// so the empty string has to be ruled out explicitly.
const parseCount = (text) => {
  const trimmed = String(text).trim()
  if (!/^\d+$/.test(trimmed)) return null
  return parseInt(trimmed, 10)
}

// +1 / -1 like PlusMinusButton, plus 'Aseta', which writes an exact value through a dialog.
//
// onSet(value) may return a promise resolving to { error, message } - the shape the
// Firebase wrapper returns instead of throwing. On error the dialog stays open and says so,
// so the admin's value is not silently lost.
function PlusMinusSetButton(props) {
  const { value, onPlus, onMinus, onSet, loading } = props
  const [open, setOpen] = React.useState(false)
  const [text, setText] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState(null)

  const parsed = parseCount(text)

  const openDialog = () => {
    setText(value === null || value === undefined ? '' : String(value))
    setError(null)
    setOpen(true)
  }

  const closeDialog = () => {
    if (!saving) setOpen(false)
  }

  const save = async () => {
    if (parsed === null || saving) return
    setSaving(true)
    setError(null)
    const result = await onSet(parsed)
    setSaving(false)
    if (result && result.error) {
      setError(result.message || 'Tallennus epäonnistui')
    } else {
      setOpen(false)
    }
  }

  return (
    <>
      {loading ? (
        <CircularProgress size={10} />
      ) : (
        <>
          <AddCircleOutlineIcon fontSize='small' onClick={onPlus} />
          {' / '}
          <RemoveCircleOutlineIcon fontSize='small' onClick={onMinus} />
          {' / '}
          <Tooltip title='Aseta'>
            <EditOutlinedIcon fontSize='small' onClick={openDialog} sx={{ cursor: 'pointer' }} />
          </Tooltip>
        </>
      )}
      <Dialog open={open} onClose={closeDialog} maxWidth='xs' fullWidth>
        <DialogTitle>Aseta jäljellä olevat kerrat</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            type='number'
            label='Jäljellä (kertaa)'
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save()
            }}
            inputProps={{ min: 0, step: 1 }}
            error={parsed === null}
            helperText={parsed === null ? 'Anna kokonaisluku 0 tai suurempi' : ' '}
            disabled={saving}
            sx={{ mt: 1 }}
          />
          <Button outline color='secondary' size='sm' onClick={() => setText('0')} disabled={saving}>
            Aseta nollaan
          </Button>
          {error ? (
            <Alert severity='error' sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button outline color='success' size='sm' onClick={save} disabled={parsed === null || saving}>
            {saving ? <CircularProgress size={12} /> : 'Tallenna'}
          </Button>
          <Button outline color='primary' size='sm' onClick={closeDialog} disabled={saving}>
            Peruuta
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PlusMinusSetButton
